/**
 * Cowichan Wine Guide - API Service
 * Connects to the Wine Route Planner API at n8nn8n.ca
 */

const API_BASE = 'https://app.n8nn8n.ca';

// ============================================
// Visited Wineries Memory (localStorage)
// Tracks winery IDs visited in the past 14 days
// ============================================

const VISITED_KEY = 'cowichan_visited_wineries';
const VISITED_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function readVisitedStore() {
  try {
    const raw = localStorage.getItem(VISITED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeVisitedStore(entries) {
  try {
    localStorage.setItem(VISITED_KEY, JSON.stringify(entries));
  } catch {}
}

export function saveVisitedWineries(stops) {
  const now = Date.now();
  const existing = readVisitedStore();
  const updated = [...existing];

  for (const stop of stops) {
    if (!stop.id) continue;
    const idx = updated.findIndex(e => e.id === stop.id);
    if (idx >= 0) {
      updated[idx].ts = now;
    } else {
      updated.push({ id: stop.id, ts: now });
    }
  }

  const capped = updated.sort((a, b) => b.ts - a.ts).slice(0, 30);
  writeVisitedStore(capped);
}

export function clearVisitedWineries() {
  writeVisitedStore([]);
}

function getExcludedWineryIds() {
  const cutoff = Date.now() - VISITED_EXPIRY_MS;
  const all = readVisitedStore();
  const active = all.filter(e => e.ts > cutoff);

  if (active.length !== all.length) {
    writeVisitedStore(active);
  }

  return active.map(e => e.id).filter(Boolean);
}

/**
 * Submit a winery rating
 */
export async function submitWineryRating(wineryId, rating, comment = '') {
  const response = await fetch(`${API_BASE}/webhook/cowichan-winery-rating`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      winery_id: wineryId,
      rating,
      comment,
      submitted_at: new Date().toISOString()
    }),
  });

  if (!response.ok) {
    const error = new Error('Failed to submit rating');
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Get average rating for a winery
 */
export async function getWineryRating(wineryId) {
  try {
    const response = await fetch(
      `${API_BASE}/webhook/cowichan-get-winery-rating?wineryId=${wineryId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );

    if (!response.ok) return { average: null, count: 0 };
    return response.json();
  } catch {
    return { average: null, count: 0 };
  }
}

/**
 * Get all active wineries for a specific ferry island region
 */
export async function getIslandWineries(regionKey) {
  try {
    const response = await fetch(`${API_BASE}/webhook/cowichan-wineries/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: 'all' }),
    });
    if (!response.ok) return [];
    const data = await response.json();
    const all = data.wineries || [];
    const filtered = all.filter(w => w.active !== false && w.region_key === regionKey);
    return transformWineryList(filtered);
  } catch {
    return [];
  }
}

/**
 * Check for wildfires and evacuation alerts near the user's location and route
 */
export async function checkSafety(lat, lng, routeStops = []) {
  try {
    const response = await fetch(`${API_BASE}/webhook/okanagan-safety-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat,
        lng,
        route_stops: routeStops.map(s => ({ lat: s.lat, lng: s.lng })),
      }),
    });
    if (!response.ok) return { severity: 'none', alerts: [] };
    return response.json();
  } catch {
    return { severity: 'none', alerts: [] };
  }
}

/**
 * Get a wine route based on duration and region
 */
export async function getWineRoute(routeType, region, startLocation = null, preferences = null, source = 'direct') {
  const requestBody = {
    route_type: routeType,
    region: region.toLowerCase(),
    source,
  };

  if (startLocation && startLocation.lat && startLocation.lng) {
    requestBody.start_location = {
      lat: startLocation.lat,
      lng: startLocation.lng
    };
  }

  const excludedIds = getExcludedWineryIds();
  if (excludedIds.length > 0) {
    requestBody.excluded_winery_ids = excludedIds;
  }

  if (preferences) {
    if (preferences.wineType && preferences.wineType !== 'any') {
      requestBody.wine_type = preferences.wineType;
    }
    if (preferences.selectedWines && preferences.selectedWines.length > 0) {
      requestBody.selected_wines = preferences.selectedWines;
    }
    if (preferences.amenities && preferences.amenities.length > 0) {
      requestBody.amenities = preferences.amenities;
    }
  }

  const response = await fetch(`${API_BASE}/webhook/cowichan-wine-route`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = new Error('Failed to fetch wine route');
    error.status = response.status;
    throw error;
  }

  const data = await response.json();

  if (data.success === false && data.error === 'no_wineries_found') {
    if (requestBody.excluded_winery_ids && requestBody.excluded_winery_ids.length > 0) {
      writeVisitedStore([]);
      throw new Error('You\'ve visited all the wineries matching your preferences! Your history has been reset — tap Build again for a fresh route.');
    }
    throw new Error('No wineries found for this region and preferences. Try selecting a different area or fewer wine filters.');
  }

  const route = data.route;

  if (!route || !route.stops) {
    throw new Error('Invalid route data received');
  }

  return {
    route_name: route.type || 'Wine Route',
    total_stops: route.totalStops || route.stops.length,
    estimated_time: route.estimatedTime?.total || route.estimatedTime || 'Varies',
    stops: route.stops.map((stop, index) => ({
      id: stop.id,
      stop_number: stop.stop || index + 1,
      name: stop.name || 'Unknown Winery',
      address: stop.address || '',
      latitude: stop.coordinates?.lat || 0,
      longitude: stop.coordinates?.lng || 0,
      phone: stop.phone || '',
      website: stop.website || '',
      image_url: stop.card?.imageUrl || '',
      revised_description: stop.card?.description || '',
      hours_text: stop.card?.hours || '',
      events: stop.card?.events || '',
      features: stop.card?.features || [],
      vineyard_experiences: stop.card?.experiences || [],
      wines_offered: stop.card?.wines || [],
      restaurant_open: stop.card?.restaurantOpen || false,
      has_restaurant: stop.card?.hasRestaurant || false,
      tasting_fee: stop.card?.tastingFee || null,
      tasting_fee_updated_at: stop.card?.tastingFeeUpdatedAt || null,
      reservation_required: stop.card?.reservationRequired ?? null,
      food_service: stop.card?.foodService || null,
      region: formatRegionName(region)
    }))
  };
}

/**
 * Search for wineries by name or keyword
 */
export async function searchWineries(query) {
  const response = await fetch(`${API_BASE}/webhook/cowichan-wineries/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ region: 'all' }),
  });

  if (!response.ok) {
    throw new Error('Failed to search wineries');
  }

  const data = await response.json();
  const allWineries = data.wineries || [];

  const searchLower = query.toLowerCase();
  const filtered = allWineries.filter(w =>
    w.name?.toLowerCase().includes(searchLower) ||
    w.address?.toLowerCase().includes(searchLower) ||
    w.region_key?.toLowerCase().includes(searchLower)
  );

  return transformWineryList(filtered);
}

/**
 * Get wine varieties available in a region
 */
export async function getWinesByRegion(region) {
  try {
    const response = await fetch(`${API_BASE}/webhook/cowichan-wines-by-region`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: region.toLowerCase() }),
    });

    if (!response.ok) return getDefaultWineList();

    const data = await response.json();
    return {
      reds: data.reds || [],
      whites: data.whites || []
    };
  } catch {
    return getDefaultWineList();
  }
}

/**
 * Default wine varieties for Cowichan Valley
 * Known for citrusy whites and soft reds
 */
function getDefaultWineList() {
  return {
    reds: [
      { id: 'Pinot Noir', name: 'Pinot Noir' },
      { id: 'Gamay Noir', name: 'Gamay Noir' },
      { id: 'Marechal Foch', name: 'Marechal Foch' },
      { id: 'Cabernet Franc', name: 'Cabernet Franc' },
      { id: 'Dornfelder', name: 'Dornfelder' },
    ],
    whites: [
      { id: 'Pinot Gris', name: 'Pinot Gris' },
      { id: 'Gewürztraminer', name: 'Gewürztraminer' },
      { id: 'Ortega', name: 'Ortega' },
      { id: 'Pinot Blanc', name: 'Pinot Blanc' },
      { id: 'Chardonnay', name: 'Chardonnay' },
      { id: 'Riesling', name: 'Riesling' },
    ]
  };
}

// ============================================
// Helper Functions
// ============================================

function transformWinery(winery) {
  return {
    id: winery.id,
    name: winery.name,
    address: winery.address,
    latitude: winery.latitude,
    longitude: winery.longitude,
    phone: winery.phone,
    website: winery.website,
    image_url: winery.imageUrl || winery.image_url,
    description: winery.description,
    revised_description: winery.revised_description,
    hours_text: winery.hours || winery.hours_text,
    events: winery.events,
    region: winery.region,
    vineyard_experiences: winery.vineyard_experiences || [],
    grapes_grown: winery.grapes_grown || [],
    wines_offered: winery.wines_offered || [],
    features: winery.features || [],
    reservation_required: winery.reservation_required,
    food_service: winery.food_service || null,
    tasting_fee: winery.tasting_fee || null,
    tasting_fee_updated_at: winery.tasting_fee_updated_at || null,
    restaurant_open: winery.restaurant_open || false,
    has_restaurant: winery.has_restaurant || false,
    hours_structured: winery.hours_structured,
    last_verified: winery.last_verified,
  };
}

function transformWineryList(wineries) {
  return wineries.map(transformWinery);
}

function formatRegionName(region) {
  const regionNames = {
    'comox-valley':       'Comox Valley',
    'denman-island':      'Denman Island',
    'hornby-island':      'Hornby Island',
    'central-island':     'Central Island',
    'central-cowichan':   'Central Cowichan',
    'south-cowichan':     'South Cowichan',
    'south-island':       'South Island',
    'salt-spring-island': 'Salt Spring Island',
    'pender-island':      'Pender Island',
    'saturna-island':     'Saturna Island',
  };
  return regionNames[region.toLowerCase()] || region;
}

// ============================================
// Event Tracking
// ============================================

const SESSION_ID_KEY = 'cowichan_session_id';

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

function getDeviceType() {
  return /Mobi|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

export function logWineryEvent(eventType, winery = null, region = null) {
  try {
    fetch(`${API_BASE}/webhook/cowichan-winery-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        winery_id: winery?.id || null,
        winery_name: winery?.name || null,
        region: region || winery?.region || null,
        device_type: getDeviceType(),
        session_id: getSessionId(),
      }),
    }).catch(() => {});
  } catch {}
}

export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
}

function toRad(deg) {
  return deg * (Math.PI / 180);
}

// ============================================
// Route Type Helpers
// ============================================

export const ROUTE_TYPES = {
  '2-hour': {
    label: '2 Hours',
    description: '2 wineries, quick tasting tour',
    stops: 2,
    icon: '🍷'
  },
  'half-day': {
    label: 'Half Day',
    description: '4 wineries, leisurely exploration',
    stops: 4,
    icon: '🍇'
  },
  'full-day': {
    label: 'Full Day',
    description: '6 wineries, complete adventure',
    stops: 6,
    icon: '🏆'
  }
};

export const REGIONS = [
  { value: 'comox-valley',       label: 'Comox Valley',       description: 'Courtenay & Comox' },
  { value: 'denman-island',      label: 'Denman Island',      description: 'Ferry from Buckley Bay' },
  { value: 'hornby-island',      label: 'Hornby Island',      description: 'Ferry: Buckley Bay → Denman → Hornby' },
  { value: 'central-island',     label: 'Central Island',     description: 'Nanaimo' },
  { value: 'central-cowichan',   label: 'Central Cowichan',   description: 'Duncan & North Cowichan' },
  { value: 'south-cowichan',     label: 'South Cowichan',     description: 'Cobble Hill & Mill Bay' },
  { value: 'south-island',       label: 'South Island',       description: 'Saanich Peninsula & Victoria' },
  { value: 'salt-spring-island', label: 'Salt Spring Island', description: 'Ferry from Swartz Bay or Tsawwassen' },
  { value: 'pender-island',      label: 'Pender Island',      description: 'Ferry from Swartz Bay or Tsawwassen' },
  { value: 'saturna-island',     label: 'Saturna Island',     description: 'Ferry from Swartz Bay — most remote' },
];
